import React, { useEffect } from 'react';
import { Select, Modal } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';
import { XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import dayjs from 'dayjs';
import AnomalyIcon from '../assets/Anomaly.svg';
import '../styles/Proprieta.css';

const Anomaly = ({ arco, selectedDate, setSelectedDate, selectedData, radarSelected }) => {
  const [selectedRoadIndex, setSelectedRoadIndex] = React.useState(0);
  const [availableDates, setAvailableDates] = React.useState([]);
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Group by hash if it's an array - do this before any returns
  const isMultipleRoads = Array.isArray(arco) && arco.length > 0;
  let roads = [];

  if (isMultipleRoads) {
    const roadsByHash = {};
    arco.forEach(element => {
      const hash = element.hash;
      if (!roadsByHash[hash]) {
        roadsByHash[hash] = [];
      }
      roadsByHash[hash].push(element);
    });

    roads = Object.keys(roadsByHash).map(hash => ({
      hash,
      elements: roadsByHash[hash]
    }));
  } else if (arco && !Array.isArray(arco)) {
    roads = [{ hash: arco.hash, elements: [arco] }];
  }

  // Get available dates from roads data - MUST be called before any returns
  useEffect(() => {
    if (!roads || roads.length === 0) {
      setAvailableDates([]);
      return;
    }

    const dates = new Set();
    roads.forEach(road => {
      road.elements?.forEach(el => {
        if (el.date) {
          dates.add(dayjs(el.date).format('YYYY-MM-DD'));
        }
      });
    });

    setAvailableDates(Array.from(dates).sort());
  }, [roads]);

  // Set first date as default if no date is selected
  useEffect(() => {
    if (!selectedDate && availableDates.length > 0) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates, selectedDate, setSelectedDate]);

  // Only show for roads, not radar
  if (radarSelected && (!arco || arco.length === 0)) {
    return (
      <div className="proprieta-wrapper">
        <div className="proprieta-content" style={{ padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ color: '#999', fontSize: '14px' }}>
            Seleziona una strada sulla mappa per visualizzare le anomalie di traffico.
          </p>
        </div>
      </div>
    );
  }

  if (!arco || (Array.isArray(arco) && arco.length === 0)) {
    return (
      <div className="proprieta-wrapper">
        <div className="proprieta-content" style={{ padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ color: '#999', fontSize: '14px' }}>
            Seleziona una strada sulla mappa per visualizzare le anomalie di traffico.
          </p>
        </div>
      </div>
    );
  }

  // Detect anomalies based on incident travel time vs normal travel time
  const detectAnomalies = () => {
    if (!selectedDate) {
      return { chartData: selectedData || [], anomalies: [] };
    }

    // Filter to show only the selected road when multiple roads are selected
    const roadsToShow = roads.length > 1 ? [roads[selectedRoadIndex]] : roads;
    let anomalies = [];
    const dataPoints = [];

    roadsToShow.forEach((road, roadIndex) => {
      const roadElements = road.elements || [];

      const filteredElements = selectedDate
        ? roadElements.filter(el => {
            if (el.date) {
              const elDate = dayjs(el.date).format('YYYY-MM-DD');
              return elDate === selectedDate;
            }
            return false;
          })
        : roadElements;

      filteredElements.forEach(el => {
        let time;
        // Prioritize timestamp (Unix timestamp) as it's the most reliable
        if (el.timestamp) {
          time = dayjs.unix(el.timestamp).format('HH:mm');
        } else if (el.datetime && !el.datetime.startsWith('1970')) {
          time = dayjs(el.datetime).format('HH:mm');
        } else if (el.hour !== undefined && el.hour < 24) {
          const minute = el.minute !== undefined ? el.minute : 0;
          time = `${el.hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        } else {
          return;
        }

        // Calculate anomaly - support both new and old formats
        let anomalyPercentage = 0;
        let normalTime = 0;
        let incidentTime = 0;

        if (el.anomaly !== undefined) {
          // New format: anomaly is already a percentage (0-100)
          anomalyPercentage = parseFloat(el.anomaly);
          normalTime = el.travel_time_in_seconds || 0;
          incidentTime = normalTime; // In new format, we don't have separate incident time
        } else {
          // Old format: calculate from travel times
          normalTime = el.travel_time_in_seconds || 0;
          incidentTime = el.live_traffic_incidents_travel_time_in_seconds || 0;

          // Shows the increase from normal to incident time
          const anomalyDifference = incidentTime - normalTime;
          if (normalTime > 0) {
            anomalyPercentage = Math.round((anomalyDifference / normalTime) * 100);
          }
        }

        const hasIncident = Math.abs(anomalyPercentage) > 5; // Anomaly if difference > 5%

        if (hasIncident) {
          anomalies.push({
            time,
            strada: roads.length > 1 ? selectedRoadIndex + 1 : 1,
            normalTime,
            incidentTime,
            delay: anomalyPercentage
          });
        }

        // For display purposes, we show anomaly percentage directly
        const displayValue = Math.abs(anomalyPercentage);

        dataPoints.push({
          time,
          tempo_normale: displayValue,
          tempo_incidente: displayValue,
          hasIncident,
          roadIndex
        });
      });
    });

    // Sort by time
    dataPoints.sort((a, b) => {
      const [aHour, aMin] = a.time.split(':').map(Number);
      const [bHour, bMin] = b.time.split(':').map(Number);
      return (aHour * 60 + aMin) - (bHour * 60 + bMin);
    });

    return {
      chartData: dataPoints,
      anomalies
    };
  };

  const { chartData: chartDataToDisplay, anomalies } = detectAnomalies();
  const colors = ['#A1E430', '#8B7FD9'];

  // Render chart component
  const renderChart = (height = 300) => (
    <>
      {chartDataToDisplay?.length > 0 ? (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartDataToDisplay}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="time"
              tick={{ fontSize: 11, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
              interval="preserveStartEnd"
              ticks={['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '23:59']}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
              label={{ value: '%', position: 'insideLeft', style: { fontSize: 12, fill: '#666' } }}
            />
            <Tooltip
              formatter={(value, name) => {
                return [`${value}%`, name];
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="tempo_normale"
              stroke={colors[selectedRoadIndex % colors.length]}
              strokeWidth={2}
              name={roads.length > 1 ? `Strada ${selectedRoadIndex + 1}` : 'Anomaly'}
              dot={{ r: 3 }}
              connectNulls={true}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="chart-no-data">
          <p>⚠️ Nessun dato disponibile per questa strada nella data selezionata.</p>
          <p style={{ fontSize: '12px', marginTop: '8px', color: '#999' }}>
            Prova a selezionare un'altra data.
          </p>
        </div>
      )}
    </>
  );

  // Check if road has any data at all
  const hasAnyData = availableDates.length > 0;

  return (
    <>
    <div className="proprieta-wrapper">
          <div className="proprieta-title">
            <img src={AnomalyIcon} alt="Anomaly" className="proprieta-icon-img" />
            <h3>Anomaly</h3>
          </div>

        {/* Road selector buttons - always visible when 2 roads are selected */}
        {roads.length > 1 && (
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px 0',
            marginBottom: '8px'
          }}>
            <button
              onClick={() => setSelectedRoadIndex(0)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: selectedRoadIndex === 0 ? '2px solid #A1E430' : '1px solid #ddd',
                background: selectedRoadIndex === 0 ? '#A1E43020' : 'white',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: selectedRoadIndex === 0 ? 'bold' : 'normal'
              }}
            >
              Strada 1
            </button>
            <button
              onClick={() => setSelectedRoadIndex(1)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: selectedRoadIndex === 1 ? '2px solid #8B7FD9' : '1px solid #ddd',
                background: selectedRoadIndex === 1 ? '#8B7FD920' : 'white',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: selectedRoadIndex === 1 ? 'bold' : 'normal'
              }}
            >
              Strada 2
            </button>
          </div>
        )}

        {/* Always show date selector and chart area */}
        <div className="proprieta-content anomaly-content">
          <p className="proprieta-description anomaly-description">
            Seleziona la data per la quale vuoi visualizzare i valori.
          </p>
          {hasAnyData ? (
            <Select
              placeholder="Seleziona data"
              className="anomaly-date-picker"
              value={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              getPopupContainer={(trigger) => trigger.parentElement}
              popupStyle={{ zIndex: 9999 }}
              style={{ borderRadius: "20px" }}
            >
              {availableDates.map(date => (
                <Select.Option key={date} value={date}>
                  {dayjs(date).format('DD/MM/YYYY')}
                </Select.Option>
              ))}
            </Select>
          ) : (
            <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
              Nessuna data disponibile per questa strada.
            </div>
          )}
        </div>

        {/* Anomaly Detection Chart Card - Always show, with no-data message if needed */}
        <div style={{ position: 'relative' }}>
          {chartDataToDisplay?.length > 0 && (
            <button
              onClick={() => setIsExpanded(true)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                width: '36px',
                height: '36px',
                backgroundColor: 'rgba(24, 144, 255, 0.9)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                zIndex: 100,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(64, 169, 255, 1)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.9)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title="Espandi grafico"
            >
              <ExpandOutlined />
            </button>
          )}
          {renderChart()}
        </div>
    </div>

    {/* Expanded Chart Modal */}
    <Modal
      open={isExpanded}
      onCancel={() => setIsExpanded(false)}
      footer={null}
      width="95%"
      centered
      title="Anomaly - Vista Espansa"
      destroyOnClose
      zIndex={9999}
      style={{ top: 20 }}
    >
      <div style={{ padding: '20px 0' }}>
        {renderChart(500)}
      </div>
    </Modal>
    </>
  );
};

export default Anomaly;
